
import OpenAI from 'openai';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

async function getAIResponse(data) {
  try {
    const systemPrompt = `
      Você é AXIS, uma IA especialista em endocrinologia e análise hormonal.
      
      DADOS DO PACIENTE:
      ${formatPatientData(data)}

      RESULTADOS LABORATORIAIS:
      ${formatLabResults(data.examValues)}

      ANÁLISE DA CALCULADORA:
      ${formatCalculatorAnalysis(data.analysisResults)}

      ALERTAS GERADOS:
      ${formatAlerts(data.alerts)}

      DIRETRIZES DE RESPOSTA:
      1. Use linguagem técnica precisa mas compreensível
      2. Baseie-se nas diretrizes mais recentes
      3. Cite referências científicas
      4. Forneça recomendações práticas
      5. Indique quando necessário avaliação médica

      ESTRUTURA DA RESPOSTA:
      1. Status do Eixo Hormonal
      2. Correlações Importantes
      3. Possíveis Disfunções
      4. Recomendações Específicas
      5. Exames Complementares
      6. Referências Bibliográficas
    `;

    const completion = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: data.userQuery || "Analise meus resultados hormonais em detalhes."
        }
      ],
      temperature: 0.7,
      max_tokens: 2000,
      presence_penalty: 0.3,
      frequency_penalty: 0.3
    });

    return {
      status: "success",
      content: completion.choices[0].message.content,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error("OpenAI API Error:", error);
    throw new Error(`Falha na análise da IA: ${error.message}`);
  }
}

function formatPatientData(data) {
  const { gender, age, clinicalData } = data;
  return `
    Gênero: ${gender}
    Idade: ${age}
    ${clinicalData ? `
    Dados Clínicos:
    ${Object.entries(clinicalData)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n')}
    ` : ''}
  `;
}

function formatLabResults(examValues) {
  if (!examValues) return "Sem dados laboratoriais";
  
  return Object.entries(examValues)
    .map(([hormone, value]) => {
      const unit = getHormoneUnit(hormone);
      const reference = getHormoneReference(hormone);
      const status = getHormoneStatus(value, reference);
      
      return `${hormone}: ${value} ${unit}
      Referência: ${reference.min}-${reference.max} ${unit}
      Status: ${status.toUpperCase()}`;
    })
    .join('\n\n');
}

function formatCalculatorAnalysis(results) {
  if (!results) return "Sem análise disponível";

  return `
    Status Geral: ${results.status || 'N/A'}
    Severidade: ${results.severity || 'N/A'}
    
    Correlações Identificadas:
    ${results.correlations?.map(c => 
      `- ${c.title}
       ${c.description}
       Severidade: ${c.severity}
       ${c.recommendation ? `Recomendação: ${c.recommendation}` : ''}`
    ).join('\n\n') || 'N/A'}
    
    Recomendações Gerais:
    ${results.recommendations?.join('\n') || 'N/A'}
  `;
}

function formatAlerts(alerts) {
  if (!alerts?.length) return "Sem alertas";
  
  return alerts
    .map(alert => `⚠️ ${alert.title}
    ${alert.description}
    Severidade: ${alert.severity}
    ${alert.recommendation ? `Recomendação: ${alert.recommendation}` : ''}`)
    .join('\n\n');
}

function getHormoneUnit(hormone) {
  const units = {
    testosterona: "ng/dL",
    estradiol: "pg/mL",
    lh: "mUI/mL",
    fsh: "mUI/mL",
    prolactina: "ng/mL",
    shbg: "nmol/L",
    cortisol: "μg/dL",
    dht: "ng/dL",
    amh: "ng/mL",
    progesterona: "ng/mL",
    glicemia: "mg/dL",
    insulina: "μU/mL",
    ldl: "mg/dL",
    hdl: "mg/dL",
    triglicerides: "mg/dL",
    hematocrito: "%"
  };
  
  return units[hormone.toLowerCase()] || "";
}

function getHormoneReference(hormone) {
  const references = {
    // Feminino
    estradiol_pre: { min: 46, max: 607, phase: "Fase Folicular" },
    estradiol_ovul: { min: 315, max: 1828, phase: "Pico Ovulatório" },
    estradiol_lut: { min: 161, max: 774, phase: "Fase Lútea" },
    estradiol_post: { min: 0, max: 20, phase: "Pós-Menopausa" },
    
    fsh_pre: { min: 2.8, max: 12.0, phase: "Fase Folicular" },
    fsh_ovul: { min: 12.0, max: 25.0, phase: "Pico Ovulatório" },
    fsh_lut: { min: 1.2, max: 12.0, phase: "Fase Lútea" },
    fsh_post: { min: 30.0, max: 100.0, phase: "Pós-Menopausa" },
    
    lh_pre: { min: 1.8, max: 11.8, phase: "Fase Folicular" },
    lh_ovul: { min: 7.6, max: 89.1, phase: "Pico Ovulatório" },
    lh_lut: { min: 0.6, max: 14.0, phase: "Fase Lútea" },
    lh_post: { min: 5.2, max: 62.9, phase: "Pós-Menopausa" },
    
    amh: { min: 1.0, max: 4.0, phase: "Idade Reprodutiva" },
    amh_post: { min: 0, max: 0.5, phase: "Pós-Menopausa" },
    
    // Masculino
    testosterona: { min: 240, max: 950 },
    estradiol_male: { min: 10, max: 40 },
    lh_male: { min: 1.5, max: 9.3 },
    fsh_male: { min: 1.6, max: 8.0 }
  };

  return references[hormone.toLowerCase()] || { min: 0, max: 0 };
}

function getHormoneStatus(value, reference) {
  if (!reference || !value) return "normal";
  if (value < reference.min) return "baixo";
  if (value > reference.max) return "alto";
  return "normal";
}

export { getAIResponse };
